import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, User, Menu, X } from 'lucide-react';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);
  return null;
}

const Header = ({ isAdmin, onLogout }) => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isLocalhost = () => {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < 10) {
        // Always show header when at top
        setIsVisible(true);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show header
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down and past 100px - hide header
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Close mobile menu on escape key or outside click
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    const handleResize = () => {
      if (window.innerWidth > 768 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, [mobileMenuOpen]);

    return (
    <header 
      className="header" 
      style={{
        transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'transform 0.3s ease-in-out',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000
      }}
    >
      <div className="header-content">
        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn"
          onClick={toggleMobileMenu}
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            color: '#00ff80',
            cursor: 'pointer',
            padding: '0.5rem',
            zIndex: 1001
          }}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <Link to="/" className="logo" onClick={closeMobileMenu}>
          <img 
            src="/assets/logo/vibrate-logo-horiz-red.png" 
            alt="Vibrate LA" 
            style={{
              height: '60px',
              width: 'auto'
            }}
          />
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="nav desktop-nav">
          <Link 
            to="/" 
            className={`nav-link ${isActive('/') ? 'active' : ''}`}
          >
            Home
          </Link>
          <Link 
            to="/events" 
            className={`nav-link ${isActive('/events') ? 'active' : ''}`}
          >
            Events
          </Link>
          <Link 
            to="/contact" 
            className={`nav-link ${isActive('/contact') ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            Contact
          </Link>
          
          {isAdmin ? (
            <div className="admin-section">
              <Link 
                to="/admin/dashboard" 
                className={`nav-link ${isActive('/admin/dashboard') ? 'active' : ''}`}
              >
                <User size={18} style={{ marginRight: '0.5rem' }} />
                Dashboard
              </Link>
              <button onClick={onLogout} className="logout-btn">
                <LogOut size={16} style={{ marginRight: '0.5rem' }} />
                Logout
              </button>
            </div>
          ) : (isLocalhost() && (
            <Link 
              to="/admin/login" 
              className={`nav-link ${isActive('/admin/login') ? 'active' : ''}`}
            >
              Admin
            </Link>
          ))}
        </nav>

        {/* Mobile Navigation */}
        <nav 
          className={`mobile-nav ${mobileMenuOpen ? 'mobile-nav-open' : ''}`}
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100%',
            height: '100vh',
            background: 'rgba(10, 10, 15, 0.98)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '2rem',
            transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 0.3s ease-in-out',
            zIndex: 999
          }}
        >
          <Link 
            to="/" 
            className={`mobile-nav-link ${isActive('/') ? 'active' : ''}`}
            onClick={closeMobileMenu}
            style={{
              color: isActive('/') ? '#00ff80' : 'white',
              textDecoration: 'none',
              fontSize: '2rem',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              transition: 'all 0.3s ease',
              textShadow: isActive('/') ? '0 0 20px rgba(0, 255, 128, 0.8)' : 'none'
            }}
          >
            Home
          </Link>
          <Link 
            to="/events" 
            className={`mobile-nav-link ${isActive('/events') ? 'active' : ''}`}
            onClick={closeMobileMenu}
            style={{
              color: isActive('/events') ? '#00ff80' : 'white',
              textDecoration: 'none',
              fontSize: '2rem',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              transition: 'all 0.3s ease',
              textShadow: isActive('/events') ? '0 0 20px rgba(0, 255, 128, 0.8)' : 'none'
            }}
          >
            Events
          </Link>
          <Link 
            to="/contact" 
            className={`mobile-nav-link ${isActive('/contact') ? 'active' : ''}`}
            onClick={closeMobileMenu}
            style={{
              color: isActive('/contact') ? '#00ff80' : 'white',
              textDecoration: 'none',
              fontSize: '2rem',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              transition: 'all 0.3s ease',
              textShadow: isActive('/contact') ? '0 0 20px rgba(0, 255, 128, 0.8)' : 'none'
            }}
          >
            Contact
          </Link>
          
          {isAdmin ? (
            <>
              <Link 
                to="/admin/dashboard" 
                className={`mobile-nav-link ${isActive('/admin/dashboard') ? 'active' : ''}`}
                onClick={closeMobileMenu}
                style={{
                  color: isActive('/admin/dashboard') ? '#00ff80' : 'white',
                  textDecoration: 'none',
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  transition: 'all 0.3s ease',
                  textShadow: isActive('/admin/dashboard') ? '0 0 20px rgba(0, 255, 128, 0.8)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <User size={24} />
                Dashboard
              </Link>
              <button 
                onClick={() => { onLogout(); closeMobileMenu(); }}
                style={{
                  background: 'transparent',
                  border: '2px solid #ff0080',
                  color: '#ff0080',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#ff0080';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#ff0080';
                }}
              >
                <LogOut size={20} />
                Logout
              </button>
            </>
          ) : (isLocalhost() && (
            <Link 
              to="/admin/login" 
              className={`mobile-nav-link ${isActive('/admin/login') ? 'active' : ''}`}
              onClick={closeMobileMenu}
              style={{
                color: isActive('/admin/login') ? '#00ff80' : 'white',
                textDecoration: 'none',
                fontSize: '2rem',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                transition: 'all 0.3s ease',
                textShadow: isActive('/admin/login') ? '0 0 20px rgba(0, 255, 128, 0.8)' : 'none'
              }}
            >
              Admin
            </Link>
          ))}
        </nav>
      </div>
      <ScrollToTop />
    </header>
  );
};

export default Header;
